import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import IconButton from '@mui/material/IconButton';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { useTheme } from '@mui/material/styles';
import SettingsIcon from '@mui/icons-material/Settings';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Cell,
  Pie,
  PieChart,
  PieLabelRenderProps,
  ResponsiveContainer,
  Sector,
  SectorProps,
} from 'recharts';
import CircularProgress from '@mui/material/CircularProgress';
import RWTextFieldComponent from 'components/RWTextFieldComponent';
import { NameIdObj } from 'common/models/GenericTypes';
import MultipleUserPicker from 'components/Pickers/MultipleUserPicker';
import MultipleGroupPicker from 'components/Pickers/MultipleGroupPicker';
import { Revenue } from 'common/models/Reporting/Revenue';
import { GetRevenueDataForGraph } from 'services/ReportingService';
import { Tooltip } from '@mui/material';
import { RevenueFilter } from 'common/models/Reporting/AnalyticsFilterData';

interface Props {
  id: string;
  title: string;
  sourceType: string;
  userFilterIds: string;
  reportPeriod: number;
  showConfigButton?: boolean;
  kioskToken?: string | null;
  refreshFlag?: boolean | null;
  saveConfigHandler?: (
    id: string,
    title: string,
    userFilterIds: string,
    sourceType: string,
    reportPeriod: number
  ) => void;
}

const centerY: string | number = 120;
interface DataTest {
  name: string;
  value: number;
}
const testData: DataTest[] = [{ name: 'Group A', value: 100 }];
export default function RevenueGaugeGraphComponent({
  id,
  title: titleProp,
  sourceType: sourceTypeProps,
  userFilterIds: userIdsProps,
  reportPeriod: reportPeriodProp,
  showConfigButton = true,
  kioskToken = null,
  refreshFlag = null,
  saveConfigHandler,
}: Props) {
  const [title, setTitle] = useState('');
  const [sourceType, setSourceType] = useState('');
  const [errorHandler, setErrorHandler] = useState('');
  const [reportingPeriod, setReportingPeriod] = useState<number | null>(null);
  const [showConfigDialog, setShowConfigDialog] = useState(false);
  const [selectedUsers, setSelectedUsers] = useState<number[]>([]);
  const [selectedGroups, setSelectedGroups] = useState<number[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [revenueData, setRevenueData] = useState<Revenue | null>(null);

  const t = useTheme();

  useEffect(() => {
    setTitle(titleProp);
    setReportingPeriod(reportPeriodProp);
    setSourceType(sourceTypeProps);
    if (userIdsProps) {
      if (sourceTypeProps == 'consultants') {
        setSelectedUsers(userIdsProps.split(',').map(Number));
      }

      if (sourceTypeProps == 'groups') {
        setSelectedGroups(userIdsProps.split(',').map(Number));
      }
    }
  }, [sourceTypeProps, userIdsProps, reportPeriodProp, titleProp]);

  const getRevenueData = async () => {
    if (sourceType !== '' && reportingPeriod !== null) {
      setIsLoading(true);
      let filterTypeId = 0;

      if (sourceType == 'consultants') {
        filterTypeId = 4;
      } else if (sourceType == 'groups') {
        filterTypeId = 3;
      } else if (sourceType == 'currentuser') {
        filterTypeId = 5;
      } else {
        filterTypeId = 1;
      }

      const filter = {
        reportPeriod: reportingPeriod,
        userFilterID: userIdsProps,
        userFilterTypeID: filterTypeId,
      } as RevenueFilter;
      const res = await GetRevenueDataForGraph(filter, kioskToken, setErrorHandler);
      if (res) setRevenueData(res);

      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (refreshFlag && kioskToken) {
      getRevenueData();
    }
  }, [refreshFlag, kioskToken]);

  useEffect(() => {
    !showConfigDialog && getRevenueData();
  }, [sourceType, reportingPeriod, showConfigDialog]);

  const mapIdsToString = useCallback(() => {
    let selectedIds = '';
    if (sourceType == 'consultants') {
      selectedIds = selectedUsers.join(',');
    } else if (sourceType == 'groups') {
      selectedIds = selectedGroups.join(',');
    }
    return selectedIds;
  }, [selectedUsers, selectedGroups, sourceType]);

  const onSaveConfig = useCallback(() => {
    if (reportingPeriod) {
      saveConfigHandler &&
        saveConfigHandler(id, title, mapIdsToString(), sourceType, reportingPeriod);
      setShowConfigDialog(false);
    }
  }, [saveConfigHandler, selectedUsers, selectedGroups, sourceType, title, reportingPeriod, id]);

  const sourceTypeChangeHandler = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    setSourceType(value);
  }, []);

  const onNumericValuesChange = useCallback((values: NameIdObj[], type: string) => {
    const ids = values.map((v) => v.id);
    if (type == 'consultants') {
      setSelectedUsers(ids);
    }

    if (type == 'groups') {
      setSelectedGroups(ids);
    }
  }, []);

  const pieData = useMemo<DataTest[]>(() => {
    if (revenueData) {
      return [
        { name: revenueData.target.toLocaleString(), value: revenueData.target },
        {
          name: revenueData.totalStretch.toLocaleString(),
          value: revenueData.totalStretch - revenueData.target,
        },
        {
          name: revenueData.stretchEnd.toLocaleString(),
          value: revenueData.stretchEnd - revenueData.totalStretch,
        },
      ];
    }

    return [];
  }, [revenueData]);

  const needlePosition = useMemo<{ start: number; end: number }>(() => {
    if (revenueData) {
      const needleWidth = 2;
      const { needleMarker, stretchEnd } = revenueData;
      if (needleMarker < 0) return { start: 180 + needleWidth, end: 180 - needleWidth };
      if (needleMarker > stretchEnd) return { start: 0 + needleWidth, end: 0 - needleWidth };
      const needleAngle = 180 - (needleMarker * 180) / stretchEnd;
      return { start: needleAngle + needleWidth, end: needleAngle - needleWidth };
    }

    return { start: 0, end: 0 };
  }, [revenueData]);

  const colors = useMemo(() => {
    return [t.palette.error.light, t.palette.warning.light, t.palette.success.light];
  }, [t.palette.error.light, t.palette.warning.light, t.palette.success.light]);

  const renderCustomLabel = useCallback(
    (props: PieLabelRenderProps) => {
      const { endAngle = 0, outerRadius = 0, cx = 0, cy = 0 } = props;
      const RAD = Math.PI / 180;
      const cos = Math.cos(-RAD * endAngle);
      const sin = Math.sin(-RAD * endAngle);
      const x = +cx + (+outerRadius + 10) * cos;
      const y = +cy + (+outerRadius + 10) * sin - 10;
      if (revenueData?.showLabels) {
        return (
          <text
            x={x}
            y={y}
            fill={t.palette.text.primary}
            textAnchor="start"
            dominantBaseline="central"
            fontSize={14}
          >
            {props.name}
          </text>
        );
      } else {
        return <></>;
      }
    },
    [t.palette.text.primary, revenueData]
  );

  const renderActiveShape = useCallback(
    (props: SectorProps) => {
      const { cx, cy, innerRadius, outerRadius, startAngle, endAngle, fill } = props;
      const total = revenueData ? Math.round(revenueData.total).toLocaleString() : '';
      return (
        <g>
          <text
            x={cx}
            y={cy}
            dy={8}
            textAnchor="middle"
            fill={t.palette.text.primary}
            fontWeight={600}
            fontSize={16}
          >
            {total}
          </text>
          <Sector
            cx={cx}
            cy={cy}
            innerRadius={innerRadius}
            outerRadius={outerRadius}
            startAngle={startAngle}
            endAngle={endAngle}
            fill={fill}
          />
        </g>
      );
    },
    [revenueData, t.palette.text.primary]
  );

  const handleCancel = () => {
    setTitle(titleProp);
    setReportingPeriod(reportPeriodProp);
    setSourceType(sourceTypeProps);
    if (userIdsProps) {
      if (sourceTypeProps == 'consultants') {
        setSelectedUsers(userIdsProps.split(',').map(Number));
      }

      if (sourceTypeProps == 'groups') {
        setSelectedGroups(userIdsProps.split(',').map(Number));
      }
    }
    setShowConfigDialog(false);
  };

  return (
    <>
      <Dialog open={showConfigDialog} fullWidth>
        <DialogTitle>Configure Element</DialogTitle>
        <DialogContent>
          <Stack spacing={2} mt="10px">
            <RWTextFieldComponent
              label="Title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
            />
            <TextField
              select
              label="Source Type"
              value={sourceType}
              onChange={sourceTypeChangeHandler}
              fullWidth
            >
              <MenuItem value="company">All</MenuItem>
              <MenuItem value="consultants">Consultant</MenuItem>
              <MenuItem value="currentuser">Current User</MenuItem>
              <MenuItem value="groups">Group</MenuItem>
            </TextField>
            {sourceType === 'consultants' && (
              <MultipleUserPicker
                value={selectedUsers}
                onSelect={(s) => {
                  onNumericValuesChange(s, 'consultants');
                }}
                label="Consultants"
              />
            )}
            {sourceType === 'groups' && (
              <MultipleGroupPicker
                value={selectedGroups}
                onSelect={(s) => {
                  onNumericValuesChange(s, 'groups');
                }}
                label="Groups"
              />
            )}
            <TextField
              select
              label="Reporting Period"
              value={reportingPeriod?.toString() ?? '1'}
              onChange={({ target }) => setReportingPeriod(+target.value)}
              fullWidth
            >
              <MenuItem value="1">This Month</MenuItem>
              <MenuItem value="2">This Quarter (Financial)</MenuItem>
              <MenuItem value="3">This Year (Financial)</MenuItem>
              <MenuItem value="4">Last Month (Financial)</MenuItem>
              <MenuItem value="5">Last Quarter (Financial)</MenuItem>
              <MenuItem value="6">Last Year (Financial)</MenuItem>
            </TextField>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button variant="contained" color="error" onClick={handleCancel}>
            Cancel
          </Button>
          <Button variant="contained" color="success" onClick={onSaveConfig}>
            Save
          </Button>
        </DialogActions>
      </Dialog>
      {showConfigButton && (
        <IconButton
          sx={{
            position: 'absolute',
            right: 30,
            zIndex: (t) => t.zIndex.appBar - 5,
            color: (t) => t.palette.text.disabled,
          }}
          onClick={() => setShowConfigDialog(true)}
        >
          <SettingsIcon />
        </IconButton>
      )}
      <Paper
        sx={{
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'space-between',
        }}
      >
        <Typography variant="h6" padding={1} textAlign="center" fontWeight={600} component="div">
          {title}
        </Typography>
        {isLoading && errorHandler == '' && (
          <Box m="auto">
            <CircularProgress />
          </Box>
        )}
        {errorHandler == '' && (
          <ResponsiveContainer
            width="100%"
            height={150}
            debounce={300}
            style={{ display: isLoading ? 'none' : undefined }}
          >
            <PieChart width={370} height={150} style={{ cursor: 'inherit' }}>
              <Pie
                dataKey="value"
                nameKey="name"
                startAngle={180}
                endAngle={0}
                data={pieData}
                cx="50%"
                cy={centerY}
                outerRadius={100}
                innerRadius={75}
                label={renderCustomLabel}
                labelLine={false}
              >
                <Cell fill={colors[0]} />
                <Cell fill={colors[1]} />
                <Cell fill={colors[2]} />
              </Pie>
              <Pie
                dataKey="value"
                startAngle={needlePosition.start}
                endAngle={needlePosition.end}
                data={testData}
                cx="50%"
                cy={centerY}
                outerRadius={87.5}
                innerRadius={50}
                fill={t.palette.text.primary}
                activeIndex={0}
                activeShape={renderActiveShape}
              />
            </PieChart>
          </ResponsiveContainer>
        )}
        {!isLoading && errorHandler == '' && (
          <Box display="flex" justifyContent="space-around" padding={1}>
            <Tooltip title={`Permanent - ${revenueData?.permanent.toLocaleString()}`}>
              <Typography variant="body2" component="span">
                Perm<span style={{ padding: '0 5px' }}>|</span>
                <span style={{ fontWeight: 600 }}>
                  {Math.round(revenueData?.permanent ?? 0).toLocaleString()}
                </span>
              </Typography>
            </Tooltip>
            <Tooltip title={`Contract Actuals - ${revenueData?.contractActuals.toLocaleString()}`}>
              <Typography variant="body2" component="span">
                Contract Actuals<span style={{ padding: '0 5px' }}>|</span>
                <span style={{ fontWeight: 600 }}>
                  {Math.round(revenueData?.contractActuals ?? 0).toLocaleString()}
                </span>
              </Typography>
            </Tooltip>
            <Tooltip
              title={`Contract Estimates - ${revenueData?.contractEstimated.toLocaleString()}`}
            >
              <Typography variant="body2" component="span">
                Contract Estimated<span style={{ padding: '0 5px' }}>|</span>
                <span style={{ fontWeight: 600 }}>
                  {Math.round(revenueData?.contractEstimated ?? 0).toLocaleString()}
                </span>
              </Typography>
            </Tooltip>
          </Box>
        )}
        {errorHandler != '' && (
          <Typography
            variant="subtitle2"
            padding={1}
            textAlign="center"
            fontWeight={600}
            component="div"
          >
            {errorHandler}
          </Typography>
        )}
      </Paper>
    </>
  );
}
